// =============================================================================
// io/sync/createAccount.ts — the only path that brings an account into being.
// -----------------------------------------------------------------------------
// Writes the row, then its opening fee. Ported from the "Set up account" modal's
// save handler.
//
// ── 🚨 TWO TABLES, AND THE SECOND ONE IS ALLOWED TO FAIL ──
//
// The account is one row in `accounts`; the eval/activation fee is a row in
// `account_adjustments`. They cannot be written atomically from the client, so
// one of two things has to be true when the fee insert fails:
//
//   (a) the account is rolled back — the user loses their typing and sees
//       nothing, for a fee row that is a bookkeeping detail; or
//   (b) the account SURVIVES and the screen says the fee did not save.
//
// (b), deliberately. The account is the thing the user asked for and the thing
// every other surface needs; the fee can be re-added from the account's edit
// form in ten seconds. What must never happen is the silent version — an account
// created, a fee lost, and a confident "Saved." — because the Economics tab then
// reports an ROI computed against a cost that was never recorded, and its whole
// premise is that the expense side is real. `feeWarning` is that sentence.
//
// ── ⚠ NO ROLLBACK ON THE ACCOUNT ITSELF ──
//
// If the account insert fails there is nothing to undo. If it succeeds and the
// fee fails, deleting the account to "clean up" would be a destructive write in
// a failure path, which is precisely where a bug is most likely and least
// observable. It stays.
// =============================================================================

import type { SupabaseClient } from '@supabase/supabase-js';
import type { AccountRules, AccountSource } from '../../domain/types';
import { ACCOUNT_RULES_COLUMNS } from '../../domain/types';
import type { SetupAdjustment } from '../../domain/accountSetup';
import { reportFailureAs } from '../diagnostics/errorLog';

/** `Math.random` + a clock — the old client's `uid()` shape, which its ids use. */
export function newAccountId(): string {
  return Math.random().toString(36).slice(2, 10) + Date.now().toString(36).slice(-4);
}

/** Rules keys with a real column. The rest ride `extras.rulesX`, as everywhere. */
const RULES_COLUMN_ENTRIES = Object.entries(ACCOUNT_RULES_COLUMNS)
  .filter((e): e is [string, string] => typeof e[1] === 'string');

export interface CreateAccountInput {
  name: string;
  firm: string;
  accountType: string;
  accountSize: number;
  startingBalance: number;
  currentBalance: number;
  externalId: string;
  rules: AccountRules | null;
  feeWaived: boolean;
  feeCadence: string;
  feeAmount: number;
  activatedFrom: string | null;
  /** The firm's rules page. Stored as a JSONB block, never a bare string. */
  source: AccountSource | null;
  adjustments: readonly SetupAdjustment[];
}

export interface CreateAccountResult {
  ok: boolean;
  error: string | null;
  accountId: string | null;
  /**
   * ⚠ SET WHEN THE ACCOUNT SAVED AND ITS FEE DID NOT. Not an error — the account
   * exists — but the screen must say so, or Economics silently reports an ROI
   * against a cost that was never recorded.
   */
  feeWarning?: string;
  /** The row as it would be sent, so a dry run can be read by a human. */
  columns?: Record<string, unknown>;
  extras?: Record<string, unknown>;
  adjustmentRows?: Record<string, unknown>[];
}

export interface CreateAccountOptions {
  dryRun?: boolean;
  /** Injected so a test is not at the mercy of `Math.random`. */
  idFactory?: () => string;
}

/**
 * The column patch for a NEW account.
 *
 * ⚠ `rules` may be NULL — the `custom` shape carries none by design, and every
 * rules column is then left unset rather than written as 0. A zero max-drawdown
 * is the strictest possible rule, not the absence of one, and it would put a
 * failing gauge on a brand-new account.
 */
export function createAccountColumns(
  input: CreateAccountInput, id: string,
): Record<string, unknown> {
  const patch: Record<string, unknown> = {
    id,
    name: input.name,
    firm: input.firm,
    account_type: input.accountType,
    account_size: input.accountSize,
    starting_balance: input.startingBalance,
    current_balance: input.currentBalance,
    // ⚠ Always ON for a new account, as the old client does: the balance is
    // start + trade P&L + adjustments until the user says otherwise, and a new
    // account has no trades to disagree with.
    auto_balance: true,
    status: 'active',
    external_id: input.externalId,
    // ⚠ The JSONB block, or null. Never `String(...)` — that is what rendered a
    // stored rules URL as "[object Object]" on two of Josh's accounts.
    source: input.source,
  };

  if (input.rules) {
    for (const [key, col] of RULES_COLUMN_ENTRIES) {
      patch[col] = (input.rules as unknown as Record<string, unknown>)[key];
    }
  }
  return patch;
}

/**
 * The `extras` blob for a NEW account.
 *
 * ⚠ EVERY KEY HERE HAS NO COLUMN, and three of them are load-bearing:
 * `feeWaived` (a comped account, distinct from a zero fee), the fee pair (the
 * Economics tab's only expense input), and `rulesX` — the two rule keys with no
 * column, which vanished on every save in the old client while two readers
 * defaulted them OPPOSITELY.
 */
export function createAccountExtras(input: CreateAccountInput): Record<string, unknown> {
  const extras: Record<string, unknown> = {};

  // ⚠ Written ONLY when true. A `feeWaived: false` on every account would be
  // indistinguishable from a deliberate un-waiving later.
  if (input.feeWaived) extras['feeWaived'] = true;
  else if (input.feeAmount > 0) {
    extras['feeAmount'] = input.feeAmount;
    extras['feeCadence'] = input.feeCadence;
  }

  if (input.activatedFrom) extras['activatedFrom'] = input.activatedFrom;

  if (input.rules) {
    const rulesX: Record<string, unknown> = {};
    for (const [key, col] of Object.entries(ACCOUNT_RULES_COLUMNS)) {
      if (col) continue;
      rulesX[key] = (input.rules as unknown as Record<string, unknown>)[key];
    }
    if (Object.keys(rulesX).length) extras['rulesX'] = rulesX;
  }

  return extras;
}

export async function createAccount(
  sb: SupabaseClient,
  userId: string,
  input: CreateAccountInput,
  opts: CreateAccountOptions = {},
): Promise<CreateAccountResult> {
  if (!input.name.trim()) {
    return { ok: false, error: 'A name is required.', accountId: null };
  }
  if (!userId) {
    // ⚠ Refused rather than sent. RLS would reject it anyway, but the message a
    // user gets from a policy violation says nothing they can act on.
    return { ok: false, error: 'Not signed in — refusing to create an account.', accountId: null };
  }

  const id = (opts.idFactory ?? newAccountId)();
  const columns = createAccountColumns(input, id);
  const extras = createAccountExtras(input);
  const adjustmentRows = input.adjustments.map((a) => ({
    // 🚨 MINTED HERE, AND ITS ABSENCE COST JOSH A REAL FEE ROW.
    // `account_adjustments.id` is `TEXT PRIMARY KEY` with **no default**, so an
    // insert that omits it violates the not-null constraint and the whole row is
    // rejected. The first account created through this form — "Lucid Pro 22",
    // 2026-08-09 — landed with `extras.feeAmount: 70` and no $70 adjustment.
    //
    // ⚠ The old client mints one too (`id: uid()`); this port dropped it, and
    // nothing caught that, because every test used a Supabase double that
    // accepts whatever it is handed. A fake that never enforces a constraint
    // cannot fail a row the real table refuses.
    id: (opts.idFactory ?? newAccountId)(),
    account_id: id,
    user_id: userId,
    date: a.date,
    type: a.type,
    amount: a.amount,
    note: a.note,
  }));

  if (opts.dryRun) {
    return { ok: true, error: null, accountId: id, columns, extras, adjustmentRows };
  }

  const { error } = await sb
    .from('accounts')
    .insert({ ...columns, user_id: userId, extras });

  if (error) {
    return {
      ok: false,
      error: reportFailureAs(
        'create account',
        error,
        `Couldn’t create the account: ${error.message}`,
      ),
      accountId: null,
    };
  }

  if (!adjustmentRows.length) {
    return { ok: true, error: null, accountId: id };
  }

  const { error: feeError } = await sb.from('account_adjustments').insert(adjustmentRows);

  if (feeError) {
    // ⚠ `ok: true` — the ACCOUNT EXISTS, and reporting failure would send the
    // user back to create a second one. The warning is what stops Economics
    // quietly reporting an ROI against a cost that was never recorded.
    //
    // ⚠ Logged DESPITE `ok: true`. The success is the account; the fee insert
    // genuinely failed, and this is the only place its reason survives.
    return {
      ok: true,
      error: null,
      accountId: id,
      feeWarning: reportFailureAs(
        'save account fee',
        feeError,
        `The account was created, but its ${input.adjustments[0]!.type.toLowerCase()} `
        + `did not save (${feeError.message}). Add it from the account’s Adjustments `
        + `list, or the Economics report will show a return against a cost it never saw.`,
      ),
    };
  }

  return { ok: true, error: null, accountId: id };
}
